// import { useEffect, useState, type FormEvent } from 'react'
// import {
//     Alert,
//     Autocomplete,
//     Box,
//     Button,
//     Checkbox,
//     Dialog,
//     DialogActions,
//     DialogContent,
//     DialogTitle,
//     FormControlLabel,
//     IconButton,
//     MenuItem,
//     TextField,
// } from '@mui/material'
// import CloseIcon from '@mui/icons-material/Close'
// import {
//     CURRENT_USER_ID,
//     loadUsers,
//     PRIMARY,
//     PRIORITIES,
//     STATUSES,
//     USERS,
//     type Priority,
//     type StatusId,
// } from './Tododata.ts'
// import UserAvatar from './Useravtar.tsx'
// import { ApiCretaeTodoTasks } from '../../../apiService/Core/Apicall.tsx'

// export interface NewTaskInput {
//     title: string
//     description: string
//     assigneeId: string
//     status: StatusId
//     priority: Priority
//     dueDate: string | null
//     labels: string[]
//     checklist: string[] // item texts, already trimmed
// }

// interface Props {
//     defaultStatus: StatusId
//     labelOptions: string[]
//     onClose: () => void
//     onSubmit: (input: NewTaskInput, createAnother: boolean) => void
// }

// const TITLE_MAX = 120
// const MAX_CHECKLIST_ITEMS = 50

// const defaultAssignee = () => (USERS.some((u) => u.id === CURRENT_USER_ID) ? CURRENT_USER_ID : USERS[0]?.id ?? '')

// const initialForm = (status: StatusId) => ({
//     title: '',
//     description: '',
//     assigneeId: defaultAssignee(),
//     status,
//     priority: 'medium' as Priority,
//     dueDate: '',
//     labels: [] as string[],
//     checklistText: '',
// })

// type Form = ReturnType<typeof initialForm>

// function validate(f: Form) {
//     const errors: { title?: string; dueDate?: string; checklist?: string } = {}
//     if (!f.title.trim()) errors.title = 'Task title is required'
//     else if (f.title.trim().length > TITLE_MAX) errors.title = `Keep the title under ${TITLE_MAX} characters`

//     if (!f.dueDate) errors.dueDate = 'Due date is required'

//     const items = parseChecklist(f.checklistText).length
//     if (items === 0) errors.checklist = 'Add at least one checklist item'
//     else if (items > MAX_CHECKLIST_ITEMS) errors.checklist = `A task can have up to ${MAX_CHECKLIST_ITEMS} checklist items`

//     // Description is optional, so it is not validated
//     return errors
// }

// const parseChecklist = (text: string) =>
//     text
//         .split('\n')
//         .map((l) => l.trim())
//         .filter(Boolean)

// export default function CreateTaskDialog({ defaultStatus, labelOptions, onClose, onSubmit }: Props) {
//     const [form, setForm] = useState<Form>(() => initialForm(defaultStatus))
//     const [createAnother, setCreateAnother] = useState(false)
//     const [submitted, setSubmitted] = useState(false)
//     const [saving, setSaving] = useState(false)
//     const [apiError, setApiError] = useState<string | null>(null)

//     const set = <K extends keyof Form>(key: K, value: Form[K]) => setForm((f) => ({ ...f, [key]: value }))
//     const errors = submitted ? validate(form) : {}

//     const handleSubmit = async (e: FormEvent) => {
//         e.preventDefault()
//         if (saving) return
//         setSubmitted(true)
//         if (Object.keys(validate(form)).length > 0) return

//         const input: NewTaskInput = {
//             title: form.title.trim(),
//             description: form.description.trim(),
//             assigneeId: form.assigneeId,
//             status: form.status,
//             priority: form.priority,
//             dueDate: form.dueDate,
//             labels: form.labels,
//             checklist: parseChecklist(form.checklistText),
//         }

//         // Payload for the create API. The API takes no status or labels, so those stay local for now.
//         const payload = {
//             title: input.title,
//             description: input.description,
//             assignedTo: Number(input.assigneeId),
//             dueDate: input.dueDate, // yyyy-mm-dd only
//             priority: input.priority.toUpperCase(), // LOW | MEDIUM | HIGH (| URGENT)
//             checklistItems: input.checklist.map((itemText, i) => ({ itemText, displayOrder: i + 1 })),
//         }

//         setSaving(true)
//         setApiError(null)
//         try {
//             await ApiCretaeTodoTasks(payload)
//         } catch (error: any) {
//             console.error('Error during API call:', error)
//             setApiError(error?.response?.data?.message || error?.message || 'Could not create the task. Please try again.')
//             setSaving(false)
//             return // keep the dialog open with everything the user typed
//         }
//         setSaving(false)

//         onSubmit(input, createAnother)

//         if (createAnother) {
//             setForm(initialForm(form.status))
//             setSubmitted(false)
//         }
//     }

//     return (
//         <Dialog
//             open
//             onClose={onClose}
//             fullWidth
//             maxWidth={false}
//             aria-labelledby="create-task-title"
//             PaperProps={{ component: 'form', onSubmit: handleSubmit, noValidate: true, sx: { width: 680, maxWidth: '100%', borderRadius: '16px' } }}
//         >
//             <DialogTitle id="create-task-title" sx={{ display: 'flex', alignItems: 'center', fontWeight: 700, borderBottom: '1px solid #E2DFD0' }}>
//                 Create task
//                 <IconButton aria-label="Close" onClick={onClose} sx={{ ml: 'auto' }}>
//                     <CloseIcon />
//                 </IconButton>
//             </DialogTitle>

//             <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '24px !important' }}>
//                 {apiError && <Alert severity="error">{apiError}</Alert>}
//                 <TextField
//                     autoFocus
//                     required
//                     label="Task title"
//                     placeholder="What needs to be done?"
//                     value={form.title}
//                     onChange={(e) => set('title', e.target.value)}
//                     error={!!errors.title}
//                     helperText={errors.title}
//                     inputProps={{ maxLength: TITLE_MAX + 20 }}
//                 />

//                 <TextField
//                     label="Description (optional)"
//                     placeholder="Add context for whoever picks this up"
//                     multiline
//                     minRows={3}
//                     value={form.description}
//                     onChange={(e) => set('description', e.target.value)}
//                 />

//                 <TextField select label="Assign to" value={form.assigneeId} onChange={(e) => set('assigneeId', e.target.value)}>
//                     {USERS.map((u) => (
//                         <MenuItem key={u.id} value={u.id}>
//                             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                                 <UserAvatar user={u} size={22} />
//                                 {u.name}
//                             </Box>
//                         </MenuItem>
//                     ))}
//                 </TextField>
//                 <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 2.5 }}>

//                     {/* <TextField select label="Status" value={form.status} onChange={(e) => set('status', e.target.value as StatusId)}>
//                         {STATUSES.map((s) => (
//                             <MenuItem key={s.id} value={s.id}>
//                                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                                     <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.dot }} />
//                                     {s.label}
//                                 </Box>
//                             </MenuItem>
//                         ))}
//                     </TextField> */}

//                     <TextField select label="Priority" value={form.priority} onChange={(e) => set('priority', e.target.value as Priority)}>
//                         {(Object.keys(PRIORITIES) as Priority[]).map((p) => {
//                             const { Icon, color, label } = PRIORITIES[p]
//                             return (
//                                 <MenuItem key={p} value={p}>
//                                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color }}>
//                                         <Icon fontSize="small" />
//                                         {label}
//                                     </Box>
//                                 </MenuItem>
//                             )
//                         })}
//                     </TextField>

//                     <TextField
//                         type="date"
//                         required
//                         label="Due date"
//                         value={form.dueDate}
//                         onChange={(e) => set('dueDate', e.target.value)}
//                         error={!!errors.dueDate}
//                         helperText={errors.dueDate}
//                         InputLabelProps={{ shrink: true }}
//                     />

//                     {/* <Autocomplete
//                         multiple
//                         freeSolo
//                         sx={{ gridColumn: '1 / -1' }}
//                         options={labelOptions}
//                         value={form.labels}
//                         onChange={(_, value) => set('labels', Array.from(new Set(value.map((v) => v.trim()).filter(Boolean))))}
//                         renderInput={(params) => <TextField {...params} label="Labels" placeholder="Venues, Editors" />}
//                     /> */}
//                 </Box>

//                 <TextField
//                     required
//                     label="Checklist items"
//                     placeholder="One item per line (at least one)"
//                     multiline
//                     minRows={3}
//                     value={form.checklistText}
//                     onChange={(e) => set('checklistText', e.target.value)}
//                     error={!!errors.checklist}
//                     helperText={errors.checklist ?? 'One item per line'}
//                 />
//             </DialogContent>

//             <DialogActions sx={{ px: 3, py: 2, bgcolor: '#FBFAF5', borderTop: '1px solid #E2DFD0' }}>
//                 <FormControlLabel
//                     control={
//                         <Checkbox checked={createAnother} onChange={(e) => setCreateAnother(e.target.checked)} sx={{ '&.Mui-checked': { color: PRIMARY } }} />
//                     }
//                     label="Create another"
//                     sx={{ mr: 'auto' }}
//                 />
//                 <Button onClick={onClose} disabled={saving} variant="outlined" sx={{ textTransform: 'none', fontWeight: 700, color: '#4F4D33', borderColor: PRIMARY }}>
//                     Cancel
//                 </Button>
//                 <Button type="submit" disabled={saving} variant="contained" sx={{ textTransform: 'none', fontWeight: 700, bgcolor: PRIMARY, '&:hover': { bgcolor: '#5C5A3B' } }}>
//                     {saving ? 'Creating…' : 'Create task'}
//                 </Button>
//             </DialogActions>
//         </Dialog>
//     )
// }

import { useEffect, useState, type FormEvent } from 'react'
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    MenuItem,
    TextField,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import {
    CURRENT_USER_ID,
    loadUsers,
    PRIMARY,
    PRIORITIES,
    STATUSES,
    USERS,
    USER_BY_ID,
    type Priority,
    type StatusId,
} from './Tododata.ts'
import UserAvatar from './Useravtar.tsx'
import { ApiCretaeTodoTasks } from '../../../apiService/Core/Apicall.tsx'

export interface NewTaskInput {
    title: string
    description: string
    assigneeId?: string // main assignee (first person selected)
    assignedTo: string[] // everyone assigned, main assignee first
    status: StatusId
    priority: Priority
    dueDate: string | null
    labels: string[]
    checklist: string[] // item texts, already trimmed
}

interface Props {
    defaultStatus: StatusId
    labelOptions: string[]
    onClose: () => void
    onSubmit: (input: NewTaskInput, createAnother: boolean) => void
}

const TITLE_MAX = 120
const MAX_CHECKLIST_ITEMS = 50

// const defaultAssignee = () => (USERS.some((u) => u.id === CURRENT_USER_ID) ? CURRENT_USER_ID : USERS[0]?.id ?? '')

const initialForm = (status: StatusId) => ({
    title: '',
    description: '',
    // assignedTo: defaultAssignee() ? [defaultAssignee()] : ([] as string[]),
    assignedTo: [] as string[],
    status,
    priority: 'medium' as Priority,
    dueDate: '',
    labels: [] as string[],
    checklistText: '',
})

type Form = ReturnType<typeof initialForm>

function validate(f: Form) {
    const errors: { title?: string; assignees?: string; dueDate?: string; checklist?: string } = {}
    if (!f.title.trim()) errors.title = 'Task title is required'
    else if (f.title.trim().length > TITLE_MAX) errors.title = `Keep the title under ${TITLE_MAX} characters`

    if (f.assignedTo.length === 0) errors.assignees = 'Select at least one person'

    if (!f.dueDate) errors.dueDate = 'Due date is required'

    const items = parseChecklist(f.checklistText).length
    if (items === 0) errors.checklist = 'Add at least one checklist item'
    else if (items > MAX_CHECKLIST_ITEMS) errors.checklist = `A task can have up to ${MAX_CHECKLIST_ITEMS} checklist items`

    // Description is optional, so it is not validated
    return errors
}

const parseChecklist = (text: string) =>
    text
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean)

export default function CreateTaskDialog({ defaultStatus, labelOptions, onClose, onSubmit }: Props) {
    const [form, setForm] = useState<Form>(() => initialForm(defaultStatus))
    const [createAnother, setCreateAnother] = useState(false)
    const [submitted, setSubmitted] = useState(false)
    const [saving, setSaving] = useState(false)
    const [apiError, setApiError] = useState<string | null>(null)

    const set = <K extends keyof Form>(key: K, value: Form[K]) => setForm((f) => ({ ...f, [key]: value }))
    const errors = submitted ? validate(form) : {}

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault()
        if (saving) return
        setSubmitted(true)
        if (Object.keys(validate(form)).length > 0) return

        const input: NewTaskInput = {
            title: form.title.trim(),
            description: form.description.trim(),
            // assigneeId: form.assignedTo[0],
            assignedTo: form.assignedTo,
            status: form.status,
            priority: form.priority,
            dueDate: form.dueDate,
            labels: form.labels,
            checklist: parseChecklist(form.checklistText),
        }

        // Payload for the create API. The API takes no status or labels, so those stay local for now.
        const payload = {
            title: input.title,
            description: input.description,
            assignedTo: input.assignedTo.map(Number), // main assignee
            // TODO: confirm the field name the create API expects for the full list of assignees
            // assignees: input.assignedTo.map(Number),
            dueDate: input.dueDate, // yyyy-mm-dd only
            priority: input.priority.toUpperCase(), // LOW | MEDIUM | HIGH (| URGENT)
            checklistItems: input.checklist.map((itemText, i) => ({ itemText, displayOrder: i + 1 })),
        }

        setSaving(true)
        setApiError(null)
        try {
            await ApiCretaeTodoTasks(payload)
        } catch (error: any) {
            console.error('Error during API call:', error)
            setApiError(error?.response?.data?.message || error?.message || 'Could not create the task. Please try again.')
            setSaving(false)
            return // keep the dialog open with everything the user typed
        }
        setSaving(false)

        onSubmit(input, createAnother)

        if (createAnother) {
            setForm(initialForm(form.status))
            setSubmitted(false)
        }
    }

    return (
        <Dialog
            open
            onClose={onClose}
            fullWidth
            maxWidth={false}
            aria-labelledby="create-task-title"
            PaperProps={{ component: 'form', onSubmit: handleSubmit, noValidate: true, sx: { width: 680, maxWidth: '100%', borderRadius: '16px' } }}
        >
            <DialogTitle id="create-task-title" sx={{ display: 'flex', alignItems: 'center', fontWeight: 700, borderBottom: '1px solid #E2DFD0' }}>
                Create task
                <IconButton aria-label="Close" onClick={onClose} sx={{ ml: 'auto' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '24px !important' }}>
                {apiError && <Alert severity="error">{apiError}</Alert>}
                <TextField
                    autoFocus
                    required
                    label="Task title"
                    placeholder="What needs to be done?"
                    value={form.title}
                    onChange={(e) => set('title', e.target.value)}
                    error={!!errors.title}
                    helperText={errors.title}
                    inputProps={{ maxLength: TITLE_MAX + 20 }}
                />

                <TextField
                    label="Description (optional)"
                    placeholder="Add context for whoever picks this up"
                    multiline
                    minRows={3}
                    value={form.description}
                    onChange={(e) => set('description', e.target.value)}
                />

                <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={USERS}
                    value={form.assignedTo.map((id) => USER_BY_ID[id]).filter(Boolean)}
                    onChange={(_, value) => set('assignedTo', value.map((u) => u.id))}
                    getOptionLabel={(u) => u.name}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    renderOption={(props, u) => (
                        <li {...props} key={u.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <UserAvatar user={u} size={22} />
                                {u.name}
                            </Box>
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            required={form.assignedTo.length === 0}
                            label="Assign to"
                            placeholder={form.assignedTo.length ? '' : 'Select one or more people'}
                            error={!!errors.assignees}
                            helperText={errors.assignees ?? 'The first person selected is the main assignee'}
                        />
                    )}
                />
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 2.5 }}>

                    {/* <TextField select label="Status" value={form.status} onChange={(e) => set('status', e.target.value as StatusId)}>
                        {STATUSES.map((s) => (
                            <MenuItem key={s.id} value={s.id}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.dot }} />
                                    {s.label}
                                </Box>
                            </MenuItem>
                        ))}
                    </TextField> */}

                    <TextField select label="Priority" value={form.priority} onChange={(e) => set('priority', e.target.value as Priority)}>
                        {(Object.keys(PRIORITIES) as Priority[]).map((p) => {
                            const { Icon, color, label } = PRIORITIES[p]
                            return (
                                <MenuItem key={p} value={p}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color }}>
                                        <Icon fontSize="small" />
                                        {label}
                                    </Box>
                                </MenuItem>
                            )
                        })}
                    </TextField>

                    <TextField
                        type="date"
                        required
                        label="Due date"
                        value={form.dueDate}
                        onChange={(e) => set('dueDate', e.target.value)}
                        error={!!errors.dueDate}
                        helperText={errors.dueDate}
                        InputLabelProps={{ shrink: true }}
                    />

                    {/* <Autocomplete
                        multiple
                        freeSolo
                        sx={{ gridColumn: '1 / -1' }}
                        options={labelOptions}
                        value={form.labels}
                        onChange={(_, value) => set('labels', Array.from(new Set(value.map((v) => v.trim()).filter(Boolean))))}
                        renderInput={(params) => <TextField {...params} label="Labels" placeholder="Venues, Editors" />}
                    /> */}
                </Box>

                <TextField
                    required
                    label="Checklist items"
                    placeholder="One item per line (at least one)"
                    multiline
                    minRows={3}
                    value={form.checklistText}
                    onChange={(e) => set('checklistText', e.target.value)}
                    error={!!errors.checklist}
                    helperText={errors.checklist ?? 'One item per line'}
                />
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, bgcolor: '#FBFAF5', borderTop: '1px solid #E2DFD0' }}>
                <FormControlLabel
                    control={
                        <Checkbox checked={createAnother} onChange={(e) => setCreateAnother(e.target.checked)} sx={{ '&.Mui-checked': { color: PRIMARY } }} />
                    }
                    label="Create another"
                    sx={{ mr: 'auto' }}
                />
                <Button onClick={onClose} disabled={saving} variant="outlined" sx={{ textTransform: 'none', fontWeight: 700, color: '#4F4D33', borderColor: PRIMARY }}>
                    Cancel
                </Button>
                <Button type="submit" disabled={saving} variant="contained" sx={{ textTransform: 'none', fontWeight: 700, bgcolor: PRIMARY, '&:hover': { bgcolor: '#5C5A3B' } }}>
                    {saving ? 'Creating…' : 'Create task'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}